import React, { useCallback, useEffect, useMemo } from 'react';
import {
  TablePaginatedV8,
  TableCellV8,
  EditCellV8,
  CellLeftV8,
  TableEditButtons,
  ButtonNewRecord,
  promiseModalDialog,
  toastSuccess,
} from 'motion-components';
import { useFormikContext } from 'formik';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useGetContainerTypes } from 'shared-pages/components/field-select/fs-queries';
import { id } from 'date-fns/locale';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { useMutation } from '@apollo/client';
import { DELETE_COLLECTION_POINT_REQUEST_LINE } from '../graphql';

/**
 * Example table to edit requestLines within Formik.
 */
interface RequestLine {
  id: number | null;
  containerTypeId: number | null;
  quantity: number | null;
}

export const CollectionpointRequestLinesTable = ({
  lines,
  isNew,
  canEdit,
}: {
  lines: RequestLine[];
  isNew: boolean;
  canEdit: boolean;
}) => {
  const { t } = useTranslation();
  const gridRef = React.useRef(null);
  const { appStore, tableStore } = useRootStore();
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState<any>(null);
  const [deleteContainer] = useMutation(DELETE_COLLECTION_POINT_REQUEST_LINE);
  const { values, setFieldValue, errors } = useFormikContext<any>();

  // Safely handle if requestLines is undefined

  /**
   * Table columns
   */
  const columns = useMemo(() => {
    return [
      {
        id: 'aa',
        header: t('Αα'),
        columnName: t('Αα'),
        accessorKey: 'aa',
        orderByField: 'aa',
        cell: TableCellV8,
        meta: { type: 'number' },
        size: 5,
      },
      {
        id: 'containerTypeId',
        header: t('Τύπος Συσκευασίας'),
        accessorKey: 'containerTypeId',
        orderByField: 'containertype.name',
        columnName: t('Τύπος Συσκευασίας'),
        cell: TableCellV8, // Could be a custom select cell in advanced usage
        size: 80,
        meta: {
          type: 'select',
          idKey: 'containerTypeId', // The key for the option's unique ID
          idName: 'containertype.name', // The key for the option's display text
          hook: useGetContainerTypes,
        },
      },
      {
        id: 'quantity',
        header: t('Ποσότητα'),
        accessorKey: 'quantity',
        columnName: t('Ποσότητα'),
        cell: TableCellV8,
        size: 50,
        meta: {
          type: 'number',
        },
      },
      {
        id: 'notes',
        header: t('Σημειώσεις'),
        columnName: t('Σημειώσεις'),
        accessorKey: 'notes',
        orderByField: 'notes',
        cell: TableCellV8,
        meta: {
          type: 'text',
          nullable: true,
        },
        size: 50,
      },

      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        size: 120,
        cell: (props: any) => {
          const { row, table } = props;
          return true ? EditCellV8({ row, table }) : <>-</>;
        },
      },
    ];
  }, [t]);

  /**
   * onSaveRow: fired when user "saves" an inline edit row in TablePaginatedV8
   */
  const onSaveRow = useCallback(
    async (rowIndex: number, rowOriginal: RequestLine, changes: Partial<RequestLine>) => {
      setLoading(true);
      try {
        // Make a copy of existing lines
        const updatedLines = [...lines];

        // Merge changes into the specified row
        updatedLines[rowIndex] = {
          ...rowOriginal,
          ...changes,
        };

        // Save updated array back to Formik
        setFieldValue('requestLines', updatedLines);
        return true; // Indicate success
      } catch (err) {
        console.error('Error in onSaveRow:', err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [lines, setFieldValue],
  );

  const handleRemoveLine = (rowIndex: number) => {
    const newLines = [...lines];
    newLines.splice(rowIndex, 1);
    setFieldValue('requestLines', newLines);
  };
  console.log('errors', errors);

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      <div
        className="form-group-title d-flex justify-content-between align-items-center overflow-y-hidden"
        style={{ marginBottom: '0px' }}
      >
        <h5 style={{ marginBottom: '0px' }}>{t('Τύποι Συσκευασιών')}</h5>
        <div className="d-flex">
          <TableEditButtons
            className="me-3"
            canEdit={canEdit}
            canInsert={canEdit}
            canDelete={canEdit}
            tableOptionsMeta={() => {
              if (gridRef.current) return (gridRef.current as any).table.options.meta;
              else return undefined;
            }}
            twoLines={false}
          />
        </div>
      </div>

      <TablePaginatedV8
        style={{ overflow: 'visible' }}
        id="container-id"
        loading={false}
        appStore={appStore}
        tableStore={tableStore}
        columns={columns as any}
        data={lines}
        ref={gridRef}
        onRowClick={(table) => setSelected(table.row.original)}
        rowProps={(row: any) => {
          const isRowNew = row.original.id === null;
          const rowHasError = errors.requestLines?.[row.index];
          let style = undefined;
          if (row.original.id === selected?.id) style = gridRowSelectedStyle;
          if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
          if (rowHasError) style = { ...style, color: 'red' };
          return {
            style: {
              color: isRowNew ? '#4987b7' : 'inherit',
              ...style,
            },
          };
        }}
        deleteRow={async (row) => {
          console.log('delete row', row);
          if (!row || !row.id) {
            console.error("Row is undefined or missing 'id'");
            return false;
          }
          const ans = await promiseModalDialog({
            title: 'Διαγραφή Συσκευασίας',
            text: `<div>Θέλετε να διαγράψετε την Συσκευασία<strong>${
              row?.containertype.name ?? ''
            }</strong>;</div>`,
          });
          //οptimistic update

          if (!ans) {
            return false;
          }

          const res = await deleteContainer({ variables: { id: row.id } });
          if (!res) {
            //rollback optimistic update

            return false;
          }

          toastSuccess('Διαγράφηκε η Συσκευασία');
          //remove the line from the ui?
          handleRemoveLine(row.index);
          return true;
        }}
        onNewRow={() => {
          return {
            id: null,
            aa: lines.length + 1,
            notes: '',
            containerTypeId: null,
            quantity: 0,
            containerType: { id: null, name: '' },
          };
        }}
        dataCount={lines.length}
        topBorderRadius={false}
        onSaveRow={onSaveRow}
        initSorting={[{ id: 'containerType.name', desc: false }]}
      />
      {errors && errors?.requestLines && (
        <div>
          {/* Display a general error message */}
          <small style={{ color: 'red' }}>
            {Array.isArray(errors.requestLines)
              ? 'Υπάρχουν λάθη στις γραμμές του πίνακα'
              : (errors.requestLines as any)}
          </small>

          {/* Check if there are multiple errors and map through them */}
          {Array.isArray(errors?.requestLines) && errors?.requestLines.length > 0 && (
            <ul style={{ color: 'red', marginTop: '10px' }}>
              {errors.requestLines.map(
                (error, index) =>
                  errors.requestLines[index] && (
                    <li key={index}>
                      <strong>
                        {t(`Σειρά`)} {index + 1}:
                      </strong>
                      {error && typeof error === 'object'
                        ? Object.entries(error)
                            .map(([field, message]) => `${message}`)
                            .join(', ')
                        : ''}
                    </li>
                  ),
              )}
            </ul>
          )}
        </div>
      )}
    </LoadingOverlay>
  );
};
